import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSync } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import { COLORS } from "../../../utilities/Constants"
import {
	deplacerCouverture,
	fetchCandidatsCouverture,
	lierOrdre,
	missionVersCouverture,
} from "../../../store/actions/couverturesActions"
import {
	APPEL_BADGE,
	Apercu,
	Badge,
	NIVEAU,
	Raison,
	chf,
	ui,
} from "./couverturesUi"

// Sélecteur de fiche / mission (GE-60, spec §6.4, maquette #m-select) —
// commun à « C'est une couverture… » (§5.1) et « Déplacer… » (§5.3).
// - à l'ouverture : les candidats du moteur (suggestions), triés par niveau ;
// - un seul champ qui accepte tout (n° de fiche, M-mission, plaque, nom,
//   téléphone, n° de dossier, châssis) : MÊME moteur côté serveur ;
// - filtres en pastilles, ↑ ↓ Entrée, aperçu de l'effet (plan serveur).
const APPELS = [
	["tous", "Tous"],
	["0", "Assistance"],
	["1", "Police"],
	["2", "Privé"],
]
const PERIODES = [
	["7", "7 j"],
	["30", "30 j"],
	["tout", "Tout"],
]

const SelecteurCibleModal = ({
	// "couverture" (mission → couverture) | "deplacer" | "ordre" (liaison
	// manuelle d'un ordre, Jordan 09.10)
	mode,
	missionId = null,
	couvertureId = null,
	ordreId = null,
	// mode "ordre" : proposition d'où vient la liaison (résolue avec), titre
	propositionId = null,
	titreOrdre = null,
	ciblePreselection = null,
	onClose,
	onDone,
}) => {
	const dispatch = useDispatch()
	const pour =
		mode === "couverture"
			? `mission:${missionId}`
			: mode === "ordre"
				? `ordre:${ordreId}`
				: `couverture:${couvertureId}`
	const [q, setQ] = useState("")
	const [appel, setAppel] = useState("tous")
	// null = automatique : 30 j pour les suggestions, sans limite dès qu'on
	// cherche (§3.2) ; un clic sur une pastille fixe la période.
	const [periodeChoisie, setPeriodeChoisie] = useState(null)
	const [etatFiltre, setEtatFiltre] = useState("ouvertes")
	const [data, setData] = useState(null)
	const [charge, setCharge] = useState(true)
	const [sel, setSel] = useState(ciblePreselection)
	const [plan, setPlan] = useState(null)
	const [confirmeDispatch, setConfirmeDispatch] = useState(false)
	// mode "ordre" : annuler l'ancienne mission (créée par erreur) — coché
	// par défaut quand le serveur la dit annulable.
	const [annulerAncienne, setAnnulerAncienne] = useState(true)
	const [etat, setEtat] = useState({ saving: false, saved: false })
	const [erreur, setErreur] = useState(null)
	const sequence = useRef(0)
	const sequencePlan = useRef(0)
	const listeRef = useRef(null)

	const periodeAffichee = periodeChoisie || (q.trim() ? "tout" : "30")

	// Candidats (debounce 250 ms à la frappe).
	useEffect(() => {
		const numero = ++sequence.current
		setCharge(true)
		const t = setTimeout(
			() => {
				dispatch(
					fetchCandidatsCouverture(pour, {
						q: q.trim(),
						appel,
						periode: periodeChoisie,
						etat: etatFiltre,
					}),
				).then((res) => {
					if (numero !== sequence.current) return
					setCharge(false)
					setData(res)
					// Suggestions : le défaut du moteur (unique « certaine »)
					// est présélectionné tant que rien d'autre ne l'est.
					if (res && res.defaut) setSel((s) => s || res.defaut)
				})
			},
			q ? 250 : 0,
		)
		return () => clearTimeout(t)
	}, [
		dispatch,
		pour,
		q,
		appel,
		periodeChoisie,
		etatFiltre,
		ciblePreselection,
	])

	const cible = sel
		? { type: sel.split(":")[0], id: parseInt(sel.split(":")[1]) }
		: null

	// Mission sans assistance (police, privé) : le serveur demande qui paie
	// (`assistance_requise`) — liste des partenaires + « Autre » (nom libre).
	const assistances = useSelector((state) => state.autreInfo.assistances)
	const [payeur, setPayeur] = useState({ entite: "", libre: "" })
	const [payeurDemande, setPayeurDemande] = useState(false)
	const clePayeur = `${payeur.entite}|${payeur.libre.trim()}`
	const corpsPayeur = useCallback(() => {
		if (payeur.entite && payeur.entite !== "autre")
			return { assistance_entite_id: parseInt(payeur.entite) }
		if (payeur.entite === "autre" && payeur.libre.trim())
			return { assistance_libre: payeur.libre.trim() }
		return {}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clePayeur])

	const requalifier = useCallback(
		(body) =>
			mode === "couverture"
				? missionVersCouverture(missionId, body)
				: mode === "ordre"
					? lierOrdre(ordreId, {
							...body,
							proposition: propositionId,
						})
					: deplacerCouverture(couvertureId, body),
		[mode, missionId, couvertureId, ordreId, propositionId],
	)

	// Revue 09.10 : la cible porte déjà la couverture de la même assistance
	// → « Rattacher » (coché par défaut : jamais de doublon sans le vouloir).
	const [rattachable, setRattachable] = useState(null)
	const [suivre, setSuivre] = useState(true)
	useEffect(() => {
		setRattachable(null)
		setSuivre(true)
	}, [sel])
	// Revue (C3) : dépend de l'ID seulement — un nouvel objet identique
	// ne relance pas l'aperçu (boucle).
	const rattachableId = rattachable ? rattachable.id : null
	const corpsRattacher = useCallback(
		() =>
			mode === "couverture" && rattachableId && suivre
				? { rattacher: rattachableId }
				: {},
		[mode, rattachableId, suivre],
	)
	// Revue : après un 409 (situation changée), l'aperçu est recalculé.
	const [revision, setRevision] = useState(0)
	// 3e revue (mineur liaison, à corriger vite) : après un 409, l'erreur
	// reste visible et la case « Annuler M… » garde le choix de l'utilisateur
	// — elle n'est initialisée qu'au CHANGEMENT de cible.
	const apres409 = useRef(false)
	const selInitialisee = useRef(null)

	// Aperçu : plan simulé côté serveur pour la cible sélectionnée.
	useEffect(() => {
		setPlan(null)
		if (!apres409.current) setErreur(null)
		apres409.current = false
		setConfirmeDispatch(false)
		if (!sel) return undefined
		const numero = ++sequencePlan.current
		const [type, id] = sel.split(":")
		// Simulation différée (saisie du nom libre : pas un POST par lettre).
		const t = setTimeout(() => {
			dispatch(
				requalifier({
					cible: { type, id: parseInt(id) },
					simuler: true,
					...corpsPayeur(),
					...corpsRattacher(),
				}),
			).then((res) => {
				if (numero !== sequencePlan.current) return
				if (!res || res.erreur) setErreur(res && res.erreur)
				else {
					setPlan(res)
					if (res.assistance_requise) setPayeurDemande(true)
					if (res.rattachable)
						setRattachable((r) =>
							r && r.id === res.rattachable.id
								? r
								: res.rattachable,
						)
					// Revue : mission en suspens / attente / rendez-vous
					// (23 / 25 / 27) → l'annulation n'est PAS cochée d'office.
					if (
						mode === "ordre" &&
						res.ancienne &&
						selInitialisee.current !== sel
					) {
						selInitialisee.current = sel
						setAnnulerAncienne(
							![23, 25, 27].includes(res.ancienne.etat),
						)
					}
				}
			})
		}, 250)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, sel, requalifier, corpsPayeur, corpsRattacher, revision])

	const candidats = (data && data.candidats) || []

	const peutValider =
		cible &&
		plan &&
		!plan.refus &&
		(!plan.confirmation_renforcee || confirmeDispatch) &&
		!etat.saving &&
		!etat.saved

	const valider = () => {
		if (!peutValider) return
		setErreur(null)
		setEtat({ saving: true, saved: false })
		dispatch(
			requalifier({
				cible,
				base: plan.base,
				confirmer_dispatch: confirmeDispatch,
				...corpsRattacher(),
				annuler_ancienne:
					mode === "ordre" &&
					annulerAncienne &&
					!!(plan.ancienne && plan.ancienne.annulable),
				...corpsPayeur(),
			}),
		).then((res) => {
			if (!res || res.erreur) {
				setErreur(res && res.erreur)
				setEtat({ saving: false, saved: false })
				if (res && res.erreur && res.erreur.httpStatus === 409) {
					apres409.current = true
					setRevision((n) => n + 1)
				}
				return
			}
			setEtat({ saving: false, saved: true })
			setTimeout(() => onDone(res, cible), 600)
		})
	}

	const deplacerSel = (delta) => {
		if (!candidats.length) return
		const i = candidats.findIndex((c) => c.cle === sel)
		const j =
			i < 0
				? delta > 0
					? 0
					: candidats.length - 1
				: Math.max(0, Math.min(candidats.length - 1, i + delta))
		setSel(candidats[j].cle)
		const noeud =
			listeRef.current &&
			listeRef.current.querySelector(`[data-cle="${candidats[j].cle}"]`)
		if (noeud && noeud.scrollIntoView)
			noeud.scrollIntoView({ block: "nearest" })
	}

	const onKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault()
			deplacerSel(1)
		} else if (e.key === "ArrowUp") {
			e.preventDefault()
			deplacerSel(-1)
		} else if (e.key === "Enter") {
			e.preventDefault()
			// Revue : jamais d'annulation de mission au simple Entrée.
			const annulation =
				mode === "ordre" &&
				annulerAncienne &&
				plan &&
				plan.ancienne &&
				plan.ancienne.annulable
			if (!annulation) valider()
		}
	}

	const titre =
		mode === "couverture"
			? "C'est une couverture · rattacher à…"
			: mode === "ordre"
				? titreOrdre || "Lier l'ordre à une mission / fiche…"
				: "Déplacer la couverture vers…"
	const libelleBouton =
		mode === "couverture"
			? "Rattacher"
			: mode === "ordre"
				? "Lier"
				: "Déplacer"

	return (
		<CustomModal
			title={titre}
			subtitle={(data && data.entree) || " "}
			onClose={etat.saving ? () => {} : onClose}
			showCloseButton={!etat.saving}
			maxWidth={760}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: etat.saving,
				},
				{
					title: etat.saving ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : etat.saved ? (
						<FontAwesomeIcon icon={faCheck} />
					) : (
						libelleBouton
					),
					styleName: "primary",
					onClick: valider,
					disabled: !peutValider,
				},
			]}
		>
			<div style={st.search}>
				<span role="img" aria-hidden="true">
					🔎
				</span>
				<input
					style={st.searchInput}
					placeholder="N° de fiche, M-mission, plaque, nom, téléphone, n° de dossier, châssis…"
					value={q}
					onChange={(e) => {
						setQ(e.target.value)
						setSel(null)
					}}
					onKeyDown={onKeyDown}
					autoComplete="off"
					autoFocus
				/>
				<span style={st.kbd}>↑ ↓ Entrée</span>
			</div>
			<div style={st.filters}>
				<span>Appel</span>
				<div style={ui.pills}>
					{APPELS.map(([v, l]) => (
						<button
							key={`ap_${v}`}
							type="button"
							style={ui.pill(appel === v)}
							onClick={() => setAppel(v)}
						>
							{l}
						</button>
					))}
				</div>
				<span>Période</span>
				<div style={ui.pills}>
					{PERIODES.map(([v, l]) => (
						<button
							key={`pe_${v}`}
							type="button"
							style={ui.pill(periodeAffichee === v)}
							onClick={() => setPeriodeChoisie(v)}
						>
							{l}
						</button>
					))}
				</div>
				<span>État</span>
				<div style={ui.pills}>
					{[
						["ouvertes", "Ouvertes"],
						["toutes", "Toutes"],
					].map(([v, l]) => (
						<button
							key={`et_${v}`}
							type="button"
							style={ui.pill(etatFiltre === v)}
							onClick={() => setEtatFiltre(v)}
						>
							{l}
						</button>
					))}
				</div>
			</div>
			<div style={st.tete}>
				{q.trim()
					? `Résultats pour « ${q.trim()} »`
					: "Suggestions (avant toute saisie)"}
				{charge && (
					<FontAwesomeIcon
						icon={faSync}
						spin
						style={{ marginLeft: 8, fontSize: 11 }}
					/>
				)}
			</div>
			<div style={st.cands} ref={listeRef}>
				{data && !candidats.length ? (
					<div style={st.noresult}>
						{q.trim()
							? "Aucune correspondance — essayez un n° de fiche, une plaque ou un nom"
							: "Aucune correspondance automatique — cherchez ci-dessus"}
					</div>
				) : (
					candidats.map((c) => (
						<Candidat
							key={c.cle}
							c={c}
							actif={sel === c.cle}
							onClick={() => setSel(c.cle)}
						/>
					))
				)}
			</div>
			{/* Hors du rendu conditionnel de l'aperçu : le champ garde le
			    focus pendant la simulation (revue 08.10, I11). */}
			{sel && payeurDemande && mode === "couverture" && (
				<ChoixPayeur
					payeur={payeur}
					setPayeur={setPayeur}
					assistances={assistances}
				/>
			)}
			{sel && rattachable && mode === "couverture" && (
				<label style={st.confirme}>
					<input
						type="checkbox"
						checked={suivre}
						onChange={(e) => setSuivre(e.target.checked)}
					/>
					{rattachable.libelle} — aucune couverture ajoutée
				</label>
			)}
			{!sel ? (
				<Apercu
					lignes={[]}
					vide="Sélectionnez une fiche ou une mission pour voir l'effet."
				/>
			) : plan ? (
				<>
					{plan.assistance_requise &&
					(plan.refus || "").startsWith(
						"Préciser l'assistance",
					) ? null : plan.refus ? (
						<div style={ui.erreur}>{plan.refus}</div>
					) : (
						<Apercu lignes={plan.apercu} />
					)}
					{!plan.refus &&
						mode === "ordre" &&
						plan.ancienne &&
						plan.ancienne.annulable && (
							<label style={st.confirme}>
								<input
									type="checkbox"
									checked={annulerAncienne}
									onChange={(e) =>
										setAnnulerAncienne(e.target.checked)
									}
								/>
								Annuler la mission M{plan.ancienne.id} (créée
								par erreur)
							</label>
						)}
					{!plan.refus && plan.confirmation_renforcee && (
						<label style={st.confirme}>
							<input
								type="checkbox"
								checked={confirmeDispatch}
								onChange={(e) =>
									setConfirmeDispatch(e.target.checked)
								}
							/>
							Je confirme l'annulation d'une mission déjà
							dispatchée (le dépanneur est notifié).
						</label>
					)}
				</>
			) : !erreur ? (
				<Apercu lignes={[]} vide="Calcul de l'effet…" />
			) : null}
			{erreur && (
				<div style={ui.erreur}>
					{erreur.message || "L'opération a échoué."}
				</div>
			)}
		</CustomModal>
	)
}

// Assistance qui couvre une mission sans assistance (police, privé).
// Hors du composant : pas de remontage, pas de perte de focus à la frappe.
const ChoixPayeur = ({ payeur, setPayeur, assistances }) => (
	<div style={st.payeur}>
		<label style={ui.fieldLabel}>
			Assistance qui couvre (la mission n'en porte pas)
		</label>
		<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
			<select
				style={{ ...ui.input, flex: "1 1 200px" }}
				value={payeur.entite}
				onChange={(e) =>
					setPayeur({ ...payeur, entite: e.target.value })
				}
			>
				<option value="">Choisir…</option>
				{(assistances || []).map((a) => (
					<option key={`pa_${a.id}`} value={a.id}>
						{a.label}
					</option>
				))}
				<option value="autre">Autre…</option>
			</select>
			{payeur.entite === "autre" && (
				<input
					style={{ ...ui.input, flex: "1 1 200px" }}
					placeholder="Nom de l'assistance"
					value={payeur.libre}
					maxLength={100}
					onChange={(e) =>
						setPayeur({ ...payeur, libre: e.target.value })
					}
				/>
			)}
		</div>
	</div>
)

const Candidat = ({ c, actif, onClick }) => {
	const appel = APPEL_BADGE[c.appel_type] || APPEL_BADGE[0]
	const niveau = NIVEAU[c.niveau]
	const details = [
		c.date,
		[c.plaques, c.vehicule].filter(Boolean).join(" · "),
		c.client,
		c.lieu,
		[c.etat, c.total ? chf(c.total) : null].filter(Boolean).join(" · "),
	].filter(Boolean)
	return (
		<div
			role="option"
			aria-selected={actif}
			tabIndex={-1}
			data-cle={c.cle}
			onClick={onClick}
			style={{
				...st.cand,
				border: `1px solid ${actif ? COLORS.text : COLORS.border}`,
				boxShadow: actif ? `0 0 0 1px ${COLORS.text}` : "none",
			}}
		>
			<div style={st.c1}>
				{c.titre}
				<Badge color={appel.color} bg={appel.bg}>
					{appel.label}
				</Badge>
			</div>
			{niveau ? (
				<span
					style={{
						...st.lvl,
						color: niveau.color,
						backgroundColor: niveau.bg,
					}}
				>
					{c.niveau_label}
				</span>
			) : (
				<span />
			)}
			<div style={st.c2}>
				{details.map((d, i) => (
					<span key={`d_${i}`}>{d}</span>
				))}
				{c.couvertures.map((x, i) => (
					<span key={`cv_${i}`} style={{ color: "#166534" }}>
						✓ {x.assistance}
						{x.ref_dossier ? ` ${x.ref_dossier}` : ""}
					</span>
				))}
			</div>
			{c.raisons.length > 0 && (
				<div style={st.reasons}>
					{c.raisons.map((r, i) => (
						<Raison key={`r_${i}`} r={r} />
					))}
				</div>
			)}
		</div>
	)
}

const st = {
	search: {
		display: "flex",
		gap: 8,
		alignItems: "center",
		border: `1px solid ${COLORS.borderStrong}`,
		borderRadius: 10,
		padding: "8px 12px",
		backgroundColor: COLORS.surface,
	},
	searchInput: {
		border: 0,
		outline: 0,
		fontFamily: "inherit",
		fontSize: 14,
		flex: 1,
		minWidth: 0,
		backgroundColor: "transparent",
	},
	kbd: {
		fontSize: 11,
		color: COLORS.textLight,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 4,
		padding: "1px 5px",
		whiteSpace: "nowrap",
	},
	filters: {
		display: "flex",
		gap: 14,
		flexWrap: "wrap",
		margin: "10px 0 12px",
		alignItems: "center",
		fontSize: 12,
		color: COLORS.textLight,
	},
	tete: { fontSize: 12, color: COLORS.textMuted, marginBottom: 6 },
	cands: { display: "flex", flexDirection: "column", gap: 8 },
	cand: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: "11px 14px",
		cursor: "pointer",
		display: "grid",
		gridTemplateColumns: "minmax(0, 1fr) auto",
		gap: "6px 12px",
		backgroundColor: COLORS.surface,
	},
	c1: {
		display: "flex",
		gap: 8,
		alignItems: "center",
		flexWrap: "wrap",
		fontWeight: 700,
		fontSize: 14,
	},
	c2: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		gridColumn: "1 / -1",
		display: "flex",
		gap: 12,
		flexWrap: "wrap",
	},
	lvl: {
		fontSize: 11,
		fontWeight: 700,
		borderRadius: 99,
		padding: "2px 9px",
		alignSelf: "start",
		whiteSpace: "nowrap",
	},
	reasons: {
		gridColumn: "1 / -1",
		display: "flex",
		gap: 6,
		flexWrap: "wrap",
	},
	noresult: {
		padding: 18,
		textAlign: "center",
		color: COLORS.textMuted,
		border: `1px dashed ${COLORS.borderStrong}`,
		borderRadius: 12,
		fontSize: 13,
	},
	payeur: {
		marginTop: 14,
		padding: "12px 14px",
		borderRadius: 12,
		border: "1px solid #FDE68A",
		backgroundColor: COLORS.warningLight,
	},
	confirme: {
		marginTop: 10,
		display: "flex",
		gap: 8,
		alignItems: "center",
		fontSize: 13,
		color: "#92400E",
		cursor: "pointer",
	},
}

export default SelecteurCibleModal
